import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useEffect, useRef } from 'react'
import { Checkbox, RegularModal, TextArea } from '@/components_deprecated'
import { Field } from '@/modules/companies'
import { FieldError } from '@/components/ui/field'

interface ApproveCommentModalProps {
  existingComment: string
  existingApproved: boolean
  commentRequiredOnApproval?: boolean
  commentRequiredError?: string
  onSubmit: PromptResolve<{ comment: string; approved: boolean } | null>
}

export const ApproveCommentModal: React.FC<ApproveCommentModalProps> = ({
  existingComment,
  existingApproved,
  commentRequiredOnApproval = false,
  commentRequiredError,
  onSubmit,
}) => {
  const translations = useTranslate({
    comment: 'common.comment',
    approve: 'salary.confirm-hours',
  })
  const commentInputWrapperRef = useRef<HTMLDivElement>(null)

  const { formValues: form, formChangeHandler } = useForm<{
    comment: string
    approved: boolean
  }>({
    values: {
      comment: existingComment,
      approved: !existingComment || existingApproved,
    },
  })

  useEffect(() => {
    const focusCommentInput = () => {
      const input = commentInputWrapperRef.current?.querySelector('textarea')
      if (!input) return

      input.focus()

      const selectionStart = input.value.length
      input.setSelectionRange(selectionStart, selectionStart)
    }

    focusCommentInput()

    const animationFrameId = window.requestAnimationFrame(focusCommentInput)
    const timeoutId = window.setTimeout(focusCommentInput, 250)

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      window.clearTimeout(timeoutId)
    }
  }, [])

  const commentMissing =
    commentRequiredOnApproval && form.approved && !form.comment.trim()

  return (
    <RegularModal
      title={translations.comment}
      width="400px"
      onCancel={() => onSubmit(null)}
      onSubmit={() =>
        onSubmit({ comment: form.comment, approved: form.approved })
      }
      submitDisabled={commentMissing}
      cancelOnClickOutside
    >
      <div className="mb-5" ref={commentInputWrapperRef}>
        <TextArea
          width="100%"
          value={form.comment}
          autoFocus
          onChange={formChangeHandler('comment')}
          onClick={evt => {
            evt.stopPropagation()
          }}
        />
        {commentMissing && commentRequiredError && (
          <FieldError>{commentRequiredError}</FieldError>
        )}
      </div>
      <Field>
        <label>{translations.approve}</label>
        <Checkbox
          labelPosition="left"
          checked={form.approved}
          onChange={formChangeHandler('approved')}
          alignLeft
        />
      </Field>
    </RegularModal>
  )
}
